 

import React, { useState } from 'react';
import { Table, Spinner, Form, Row, Col, Button, Alert, CardFooter } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';

import { Card, CardBody, CardHeader } from '@components/card/card';
import { ntxAPI } from '@API/axiosNeatex';
import { useTranslation } from 'react-i18next';
import type { APIResponse } from '@store/Shared/shared';

import useGetInventoryWithPermission from '@hooks/NTX/Organization/useGetInventoryWithPermission';
import useGetSubinventoryList from '@hooks/INV/Inventories/useGetSubinventoryList';
import { TreeSelect } from 'antd';
import Loading from '@components/Shared/Loader/Loading';

// ✅ Picker
import { useItemUomPicker, ItemUomPickerModal } from '@pages/INV/lookups/useItemUomPicker';
import type { ItemWithUomRow } from '@pages/INV/lookups/itemsWithUomLookup.api';

interface StockCardTransactionRow {
  itemId: string;
  itemCode: string | null;
  itemName: string | null;
  itemName2: string | null;

  transactionCode: string | null;
  transactionDate: string;

  transactionTypeId: string | null;
  transactionTypeName: string;
  transactionTypeName2: string | null;

  referenceType: string | null;
  referenceCode: string | null;
  referenceId: string | null;

  transactionQuantityIn: number;
  transactionQuantityOut: number;

  unitPrice: number | null;
  transactionQuantityTotal: number;
}

const StockCardTransactionsReport: React.FC = () => {
  const [data, setData] = useState<StockCardTransactionRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { t, i18n } = useTranslation();

  const { InventoryOptions, loading: loadingInv, error: errorInv } = useGetInventoryWithPermission();
  const { TreeSubInventoryData, loading: loadingSub, error: errorSub } = useGetSubinventoryList();

  // ✅ Filters
  const [inventoryId, setInventoryId] = useState<string | null>(null);
  const [subInventoryId, setSubInventoryId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;

  // ✅ Item & UOM Picker
  const { pickItemUom, modalProps } = useItemUomPicker();
  const [pickedItem, setPickedItem] = useState<ItemWithUomRow | null>(null);
  const [itemId, setItemId] = useState<string | null>(null);

  const getPickedItemLabel = (r: ItemWithUomRow | null) => {
    if (!r) return '';
    const name = i18n.language === 'ar' ? r.name2 || r.name : r.name || r.name2;
    const code = r.itemCode ?? '';
    return `${code} - ${name ?? ''}`.trim();
  };

  const clearPickedItem = () => {
    setPickedItem(null);
    setItemId(null);
  };

  const openItemPicker = async () => {
    const res = await pickItemUom({
      title: i18n.language === 'en' ? 'Select Item & UOM' : 'اختيار صنف ووحدة',
      params: {
        InventoryId: inventoryId || undefined,
        WithParent: true,
        WithImage: false,
        WithLots: false,
        OnlyEnabled: true
      } as any,
      pageSize: 15,
      searchMode: 'manual',
      initialSearchField: 'itemCode',
      fixedFields: ['itemCode', 'name', 'barcode', 'unitOfMeasureName'],
      dynamicFields: inventoryId ? ['quentity', 'reservedQty'] : []
    });

    if (res?.row) {
      setPickedItem(res.row);
      setItemId(res.row.itemId);
    }
  };

  const resetFilters = () => {
    clearPickedItem();
    setInventoryId(null);
    setSubInventoryId(null);
    setDateRange([null, null]);
    setData([]);
    setError(null);
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    if (dateString === '0001-01-01T00:00:00') return t('OpeningBalance');
    return new Date(dateString).toLocaleDateString();
  };

  const formatQuantity = (quantity: number): string => {
    return new Intl.NumberFormat('en-US').format(quantity ?? 0);
  };

  const formatPrice = (price: number | null): string => {
    if (price === null || price === undefined) return '-';
    return new Intl.NumberFormat(i18n.language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6 // ✅ unitPrice عندك قد يكون 0.154787
    }).format(price);
  };

  // ✅ تحديد نوع السعر حسب referenceType
  const getPriceKind = (referenceType?: string | null) => {
    const rt = (referenceType ?? '').toUpperCase();

    if (rt === 'SOP' || rt === 'SOI') return 'sale';
    if (rt === 'POR') return 'purchase';

    return 'other';
  };

  // ✅ Meta حسب referenceType (SOP/SOI/POR/SRP/SPR/MII/MOV/MOI)
  const getRefTypeMeta = (referenceType?: string | null) => {
    const rt = (referenceType ?? '').toUpperCase();

    // Sales
    if (rt === 'SOP' || rt === 'SOI') {
      return { kind: 'sale', badgeClass: 'bg-success', labelAr: 'سعر بيع', labelEn: 'Sales Price' };
    }

    // Purchase
    if (rt === 'POR') {
      return { kind: 'purchase', badgeClass: 'bg-primary', labelAr: 'سعر شراء', labelEn: 'Purchase Price' };
    }

    // Purchase Return
    if (rt === 'SRP' || rt === 'SPR') {
      return {
        kind: 'purchase_return',
        badgeClass: 'bg-warning text-dark',
        labelAr: 'مردود مشتريات',
        labelEn: 'Purchase Return'
      };
    }

    // Inventory Issue (صرف مخزني)
    if (rt === 'MII') {
      return { kind: 'issue', badgeClass: 'bg-secondary', labelAr: 'صرف مخزني', labelEn: 'Inventory Issue' };
    }

    // Transfer Order
    if (rt === 'MOV') {
      return {
        kind: 'transfer_order',
        badgeClass: 'bg-info text-dark',
        labelAr: 'أمر تحويل',
        labelEn: 'Transfer Order'
      };
    }

    // Transfer Order Issue
    if (rt === 'MOI') {
      return { kind: 'transfer_issue', badgeClass: 'bg-dark', labelAr: 'صرف أمر تحويل', labelEn: 'Transfer Issue' };
    }

    return { kind: 'other', badgeClass: 'bg-light text-dark', labelAr: 'سعر', labelEn: 'Price' };
  };

  // ✅ عرض السعر مع Badge + معالجة الأنواع اللي غالبًا سعرها 0
  const renderUnitPriceWithKind = (unitPrice: number | null, referenceType?: string | null) => {
    const meta = getRefTypeMeta(referenceType);

    // الأنواع اللي غالبًا السعر فيها 0
    const isUsuallyZero = meta.kind === 'issue' || meta.kind === 'transfer_issue';

    const isZeroOrNull = unitPrice === null || unitPrice === undefined || unitPrice === 0;

    const priceText = isUsuallyZero && isZeroOrNull ? '-' : formatPrice(unitPrice);

    const label = i18n.language === 'en' ? meta.labelEn : meta.labelAr;

    return (
      <div className='d-flex flex-column'>
        <div className='fw-bold'>{priceText}</div>

        <small className={`badge ${meta.badgeClass} align-self-start mt-1`} title={referenceType ?? ''}>
          {label}
        </small>
      </div>
    );
  };

  const fetchData = async () => {
    if (!itemId) {
      setError(t('ItemRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await ntxAPI.get<APIResponse<StockCardTransactionRow[]>>(
        `InventoryReports/GetStockCardTransactions`,
        {
          params: {
            ItemId: itemId,
            InventoryId: inventoryId,
            SubinventoryId: subInventoryId,
            StartDate: startDate ? startDate.toISOString() : null,
            EndDate: endDate ? endDate.toISOString() : null
          }
        }
      );

      const api = res.data;
      if (api?.succeeded === true) {
        setData(api.data || []);
      } else {
        setError((api as any)?.message || t('UnknownError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('UnknownError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='container mt-4'>
      <Card>
        <CardHeader className='d-flex justify-content-between align-items-center'>
          <h5 className='mb-0'>{i18n.language === 'en' ? 'Stock Card Transactions' : 'تقرير حركات بطاقة المخزون'}</h5>
          {data.length > 0 && <span className='me-2'>Showing: {data.length} transactions</span>}
        </CardHeader>

        <CardBody>
          {error && (
            <Alert variant='danger' className='mb-4'>
              {t('ErrorLoadingData')}: {error}
            </Alert>
          )}

          <Card className='mb-4'>
            <CardHeader>{t('Filters')}</CardHeader>

            <Loading loading={loadingInv || loadingSub} error={errorInv || errorSub}>
              <CardBody>
                <Form>
                  <Row className='g-3'>
                    {/* ✅ Item Picker */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>{t('itemName')}</Form.Label>

                        <div className='d-flex gap-2'>
                          <Form.Control
                            value={getPickedItemLabel(pickedItem)}
                            placeholder={t('Choose')}
                            readOnly
                            onClick={openItemPicker}
                            style={{ cursor: 'pointer' }}
                          />

                          <Button variant='outline-theme' size='sm' onClick={openItemPicker}>
                            <i className='bi bi-search' />
                          </Button>

                          {pickedItem && (
                            <Button
                              variant='outline-secondary'
                              size='sm'
                              onClick={clearPickedItem}
                              title={t('ResetFilters')}
                              aria-label='clear-item'
                            >
                              <i className='bi bi-x-lg' />
                            </Button>
                          )}
                        </div>

                        {pickedItem && (
                          <div className='text-muted small mt-1'>
                            {i18n.language === 'en' ? 'UOM:' : 'الوحدة:'}{' '}
                            {i18n.language === 'ar'
                              ? pickedItem.unitOfMeasureName2 || pickedItem.unitOfMeasureName
                              : pickedItem.unitOfMeasureName || pickedItem.unitOfMeasureName2}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* ✅ Inventory */}
                    <Col md={6}>
                      <div className='form-group mb-3'>
                        <label className='form-label'>{t('المخزن')}</label>
                        <Select
                          classNamePrefix='react-select'
                          isLoading={loadingInv}
                          isSearchable
                          isClearable
                          options={InventoryOptions}
                          value={inventoryId ? InventoryOptions?.find((f: any) => f.value === inventoryId) : null}
                          onChange={(e: any) => {
                            const v = e?.value || null;
                            setInventoryId(v);
                            setSubInventoryId(null);
                          }}
                          placeholder={t('Choose')}
                        />
                      </div>
                    </Col>

                    {/* ✅ Sub Inventory */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>{t('المخزن الفرعي')}</Form.Label>
                        <TreeSelect
                          loading={loadingSub}
                          showSearch
                          className='w-100'
                          treeLine
                          placeholder={t('Choose')}
                          allowClear
                          treeDefaultExpandAll={false}
                          value={subInventoryId}
                          onChange={(option: any) => setSubInventoryId(option === null ? null : option)}
                          treeData={TreeSubInventoryData?.filter((f: any) => f.parentId === inventoryId)}
                          treeNodeFilterProp='title'
                        />
                      </Form.Group>
                    </Col>

                    {/* ✅ Date Range */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>{t('DateRange')}</Form.Label>
                        <DatePicker
                          selectsRange
                          startDate={startDate}
                          endDate={endDate}
                          onChange={update => setDateRange(update)}
                          isClearable
                          className='form-control text-center'
                          placeholderText={t('SelectDateRange')}
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12} className='text-end'>
                      <Button variant='secondary' onClick={resetFilters} className='me-2'>
                        {t('ResetFilters')}
                      </Button>
                      <Button variant='primary' onClick={fetchData} disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner
                              as='span'
                              animation='border'
                              size='sm'
                              role='status'
                              aria-hidden='true'
                              className='me-2'
                            />
                            {t('Loading')}
                          </>
                        ) : (
                          t('LoadReport')
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Loading>
          </Card>

          {/* ✅ Result */}
          {loading ? (
            <div className='text-center my-5'>
              <Spinner animation='border' role='status'>
                <span className='visually-hidden'>{t('Loading')}</span>
              </Spinner>
              <p className='mt-2'>{i18n.language === 'en' ? 'Loading transactions...' : 'جاري تحميل الحركات...'}</p>
            </div>
          ) : data.length > 0 ? (
            <div className='table-responsive'>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>{t('Date')}</th>
                    <th>{t('TransactionCode')}</th>
                    <th>{t('TransactionType')}</th>
                    <th>{i18n.language === 'en' ? 'Reference' : 'المرجع'}</th>
                    <th>{t('ItemCode')}</th>
                    <th>{t('itemName')}</th>
                    <th>{t('QuantityIn')}</th>
                    <th>{t('QuantityOut')}</th>
                    <th>{t('TotalQuantity')}</th>
                    <th>{i18n.language === 'en' ? 'Unit Price (Type)' : 'سعر الوحدة (بيع/شراء)'}</th>
                  </tr>
                </thead>

                <tbody>
                  {data.map((r, index) => {
                    const itemName = i18n.language === 'ar' ? r.itemName2 || r.itemName : r.itemName || r.itemName2;

                    return (
                      <tr key={`${r.itemId}-${r.transactionCode}-${index}`}>
                        <td>{formatDate(r.transactionDate)}</td>

                        <td>{r.transactionCode || '-'}</td>

                        <td>
                          <div>{r.transactionTypeName || '-'}</div>
                          {r.transactionTypeName2 && <div className='text-muted small'>{r.transactionTypeName2}</div>}
                        </td>

                        <td>
                          <div className='fw-bold'>{r.referenceType || '-'}</div>
                          <div className='text-muted small'>{r.referenceCode || '-'}</div>
                        </td>

                        <td>{r.itemCode || '-'}</td>

                        <td title={itemName || ''}>
                          {itemName ? (
                            <>
                              <div>{itemName}</div>
                              {i18n.language === 'en' && r.itemName2 && (
                                <div className='text-muted small'>{r.itemName2}</div>
                              )}
                              {i18n.language === 'ar' && r.itemName && (
                                <div className='text-muted small'>{r.itemName}</div>
                              )}
                            </>
                          ) : (
                            '-'
                          )}
                        </td>

                        <td className='text-success'>
                          {r.transactionQuantityIn > 0 ? formatQuantity(r.transactionQuantityIn) : '-'}
                        </td>

                        <td className='text-danger'>
                          {r.transactionQuantityOut > 0 ? formatQuantity(r.transactionQuantityOut) : '-'}
                        </td>

                        <td className='fw-bold'>{formatQuantity(r.transactionQuantityTotal)}</td>

                        <td>{renderUnitPriceWithKind(r.unitPrice, r.referenceType)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className='text-center py-5'>
              <p>{t('NoDataAvailable')}</p>
            </div>
          )}
        </CardBody>

        {data.length > 0 && (
          <CardFooter className='text-muted'>
            {t('ReportGeneratedOn')} {new Date().toLocaleString()}
          </CardFooter>
        )}
      </Card>

      {/* ✅ Item/UOM Picker Modal */}
      <ItemUomPickerModal {...modalProps} />
    </div>
  );
};

export default StockCardTransactionsReport;
